import * as Config from "config";
import EditIcon from '@mui/icons-material/Edit';
import { styled } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from 'react';
import { KeyboardKey } from "_shared/_utilities/keyboard-keys";

const ComponentContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'nowrap',
  margin: theme.spacing(0, 3, 0, 3),
  width: '100%'
}));
const NameFields = styled('div')(() => ({
  alignItems: 'center',
  display: 'grid',
  gridTemplateColumns: '1fr',
  width: '100%'
}));

export type ProfileNameInputProps = {
  className?: string;
  name: string;
  error?: boolean;
  isLoading?: boolean;
  onNameChange: (name: string) => void;
}

/**
 * The profile's name, edited in place.
 *
 * There is deliberately no accept or cancel control here (issue #5111). This field sits inside a
 * form that has its own Save, and a second, smaller commit step inside it meant a name someone had
 * typed was only real once they had also pressed a check they had no reason to look for — leaving
 * the field any other way, or touching any other field in the form, silently put the old name back.
 * Every keystroke is reported instead, and the form's Save is the only commit there is.
 *
 * Collapsing back to the heading is a display change and nothing else: the name typed is already
 * held by the form by the time the field closes.
 */
export default function ProfileNameInput(props: Readonly<ProfileNameInputProps>) {
  const {
    className,
    error,
    isLoading,
    name,
    onNameChange
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  const [isEditingName, setIsEditingName] = useState<boolean>(false);

  useEffect(() => {
    if (
      name !== Config.defaultProfileName ||
      !isEditingName ||
      !inputRef.current
    ) return;

    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [isEditingName, name]);

  return (
    <ComponentContainer className={className}>
      {!isEditingName && (
        <Typography variant="h4" color={error ? 'error' : 'unset'}>
          {isLoading ? "Loading..." : name}
        </Typography>
      )}
      {isEditingName && (
        <NameFields>
          <TextField
            id="name"
            variant="outlined"
            slotProps={{ inputLabel: { shrink: Boolean(name) } }}
            label="Name"
            value={name}
            inputRef={inputRef}
            onChange={(event) => onNameChange(event.target.value)}
            onBlur={() => setIsEditingName(false)}
            onKeyDown={(event) => {
              if (event.key === KeyboardKey.Enter || event.key === KeyboardKey.Escape) {
                setIsEditingName(false);
              }
            }}
          />
        </NameFields>
      )}
      {!isEditingName && (
        <Tooltip title="Edit Name">
          <span>
            <IconButton
              style={{
                borderColor: 'primary'
              }}
              onClick={() => setIsEditingName(true)}
              color="primary"
              disabled={isLoading}
              aria-label="edit name"
              size="medium"
              edge="end">
              <EditIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </ComponentContainer>
  );
}
